import envConfig from '@hulu/env-config';
import type { AxiosError } from 'axios';
import jwtDecode from 'jwt-decode';
import qs from 'qs';

import { fetchRoleClaims } from '../../apis/graphql/user/queries/useFetchUserRoleList/fetchRoleClaims';
import { authorizationError } from '../../constants/global';
import { DynamicCognitoConfig } from '../../hooks/auth/useCognitoDynamicConfiguration';
import { clearSharedAuthCookiesForLogout, setCognitoVariables, syncSharedAuthCookies } from '../auth/sharedAuthCookies';
import {
  checkIsPublisherUser,
  checkIsPublisherUserFromRoles,
  decompressRoles,
  isRolesExcluded,
} from './checkIsAdminAllowedByToken';
import { Token } from './checkIsAdminAllowedByToken/checkIsPublisherUser';
import { Cognito, HelpSiteCookieHelper, LocalStorage } from './interfaces';
import type {
  GeneratedPermissions,
  IdToken,
  OauthCallbackOptions,
  RequiredPermissions,
  RequiredRole,
  RoleV4,
  UserInfo,
} from './types';
import { getUserRoleInfo, hasRequiredPermission } from './util';

interface DecodedUserTypeIdToken {
  user_type?: string;
}

export class User {
  private static generatedPermissions: GeneratedPermissions<string> | null = null;

  /**
   * Forces the user to log in.
   */
  static login(): void {
    window.location.href = Cognito.getCognitoURL();
  }

  /**
   * Sends an OAuth Callback request
   * @param options.redirectToLoginOnError Forces the user to reattempt login if an error is encountered
   */
  static async oauthCallback(options: OauthCallbackOptions = {}, useXsrfToken: boolean = true): Promise<void> {
    const code = qs.parse(window.location.search)['?code'];

    if (!code && options.redirectToLoginOnError) return this.logout();

    try {
      const { status, data } = await Cognito.sendOauthCallback(code as string, useXsrfToken);

      if (status !== 200 || !data) throw new Error(`Exchange code: ${code} for token failed, error: ${data.error}`);
      LocalStorage.setTokens(data);
      sessionStorage.removeItem(authorizationError);
      syncSharedAuthCookies(
        {
          id_token: data.id_token as string,
          access_token: data.access_token as string,
          refresh_token: data.refresh_token as string,
        },
        'admin'
      );

      // If user_type is "HUB", redirect to the HUB URL
      if (data.id_token && User.shouldRedirectToHub(data.id_token)) {
        window.location.href = envConfig.REACT_APP_REDIRECT_TO_HUB_URL;
        return;
      }

      // If not publisher user
      if (data.id_token) {
        const decodedToken = jwtDecode<Token>(data.id_token);
        const isPublisherUser = await User.resolveIsPublisherUser(decodedToken);

        if (isPublisherUser !== null && !isPublisherUser) {
          const COGNITO_CLIENT_ID = envConfig.REACT_APP_COGNITO_MARKETER_CLIENT_ID;
          const sub: string = decodedToken.sub;
          localStorage.clear();
          HelpSiteCookieHelper.unsetHelpSiteCookie();

          localStorage.setItem(`CognitoIdentityServiceProvider.${COGNITO_CLIENT_ID}.${sub}.idToken`, data.id_token);
          localStorage.setItem(
            `CognitoIdentityServiceProvider.${COGNITO_CLIENT_ID}.${sub}.accessToken`,
            data.access_token as string
          );
          localStorage.setItem(
            `CognitoIdentityServiceProvider.${COGNITO_CLIENT_ID}.${sub}.refreshToken`,
            data.refresh_token as string
          );
          localStorage.setItem(`CognitoIdentityServiceProvider.${COGNITO_CLIENT_ID}.LastAuthUser`, sub);
          window.location.replace(window.location.origin);
          return;
        }
      }

      // Otherwise, redirect to successfulRedirectHref or home page
      window.location.href = options.successfulRedirectHref ?? '/';
    } catch (error) {
      console.log(error);
      if (options.redirectToLoginOnError) return this.logout();

      const err = error as AxiosError<Record<'error', unknown>>;
      throw new Error(`Exchange code: ${code} for token failed, error: ${err?.response?.data?.error}`);
    }
  }

  /**
   * Clears any existing tokens and forces the user to login again.
   */
  static logout(): void {
    console.log('Logging out user.');
    clearSharedAuthCookiesForLogout({ preserveDeviceKey: true });
    LocalStorage.clearTokens();
    this.login();
  }

  /**
   * @returns User token IDs and content
   */
  static getUserInfo(): UserInfo {
    const tokens = LocalStorage.getTokens();

    return {
      ...tokens,
      userInfo: tokens.idToken ? jwtDecode(tokens.idToken) : null,
    };
  }

  /**
   * This function examines the user's auth tokens and compares their roles to
   * the provided list of required permissions (along with the provided role
   * definitions). It then generates an object containing boolean flags for each
   * of the requiredPermissions values. The boolean flags indicate whether the user
   * is allowed to perform the associated action.
   *
   * The permissions object is returned directly by this function, and can also be
   * retrieved at a later time by calling `User.getPermissions()`
   * @param isAuthEnabled If false, all permissions are granted by default
   * @param requiredPermissions Object containing actions and the permissions required to perform those actions
   * @param roleDefinitions List of RoleV4 definitions
   * @returns The user's generated permissions, or null if no permissions are available.
   */
  static generateUserPermissions = <PermissionName extends string>(
    isAuthEnabled: boolean,
    requiredPermissions: RequiredPermissions<PermissionName>,
    roleDefinitions: RoleV4[]
  ): GeneratedPermissions<PermissionName> | null => {
    const roleInfo = getUserRoleInfo(User.getUserInfo(), roleDefinitions);

    User.generatedPermissions = Object.entries<RequiredRole>(requiredPermissions).reduce(
      (generated, required) => ({
        ...generated,
        [required[0]]: isAuthEnabled ? hasRequiredPermission(roleInfo, required[1]) : true,
      }),
      {} as GeneratedPermissions<PermissionName>
    );

    return User.generatedPermissions;
  };

  /**
   * This function should only be called *after* permissions have been generated
   * with the `User.generateUserPermissions` function.
   * @returns The user's generated permissions, or null if no permissions are available.
   */
  static getPermissions = <PermissionName extends string>(): GeneratedPermissions<PermissionName> | null => {
    return User.generatedPermissions;
  };

  /**
   * Checks the given idToken value to determine whether it has expired.
   * @param idToken JWT Token
   * @returns Boolean result of validation check
   */
  static isIdTokenValid = (idToken?: string | null): boolean => {
    if (!idToken) return false;

    const systemTime = Math.round(Date.now() / 1000);
    const { exp } = jwtDecode<IdToken>(idToken);

    return exp !== undefined && systemTime < exp;
  };

  /**
   * Hydrates admin localStorage from shared SSO cookies when localStorage is empty.
   * Used when navigating from Genie (or another DCM-SSO app) back to the admin portal.
   */
  static hydrateAdminSessionFromCookies = async (): Promise<boolean> => {
    const { hydrateAdminAuthFromCookies } = await import('../auth/hydrateAdminAuthFromCookies');
    return hydrateAdminAuthFromCookies();
  };

  /**
   * Attempts to refresh the user's ID tokens. Forces the user to login again if their token is invalid.
   */
  static checkIdToken = async (useXsrfToken?: boolean): Promise<void> => {
    const { refreshToken, idToken } = User.getUserInfo();

    if (User.isIdTokenValid(idToken)) return;

    try {
      if (!refreshToken) throw new Error();

      const { data } = await Cognito.sendTokenRefresh(refreshToken, useXsrfToken);

      LocalStorage.setTokens(data);

      // Cognito omits refresh_token when rotation is off; reuse the existing token for SSO cookies.
      const refreshTokenForCookies = data.refresh_token ?? refreshToken;
      if (data.id_token && data.access_token && refreshTokenForCookies) {
        syncSharedAuthCookies(
          {
            id_token: data.id_token,
            access_token: data.access_token,
            refresh_token: refreshTokenForCookies,
          },
          'admin'
        );
      }
    } catch (error) {
      // If token refresh fails, we'll clear the user's tokens so they can try a fresh login
      LocalStorage.clearTokens();
    }
  };

  /**
   * Determines whether the user is a publisher (PUBLISHER_ORGANIZATION member or SELLER).
   *
   * When `roles_excluded` is true on the JWT, SELLER assignments are not present in
   * `user_role_v4` (the account-manager service trims them to keep the JWT under the
   * 8KB header size limit). In that case we fetch the full role list via the
   * `getRoleClaims` GQL query before evaluating.
   *
   * @returns true / false on success, or null if role resolution failed.
   */
  static async resolveIsPublisherUser(decodedToken: Token): Promise<boolean | null> {
    if (!isRolesExcluded(decodedToken)) {
      return checkIsPublisherUser(decodedToken);
    }

    const userId = decodedToken.ams_user_id ?? decodedToken.sub;
    if (!userId) return null;

    // Lazy-load to avoid pulling adminGraphqlAPI (and its Apollo makeVar side
    // effects) into every module that imports User — e.g. broadcast hooks in
    // tests that mock @apollo/client wholesale.
    const { adminGraphqlClient } = await import('../../apis/graphql/adminGraphqlAPI');
    const compressedRoles = await fetchRoleClaims(adminGraphqlClient, userId);
    if (!compressedRoles) return null;

    try {
      return checkIsPublisherUserFromRoles(decompressRoles(compressedRoles));
    } catch (error) {
      console.error('Failed to decompress role claims', error);
      return null;
    }
  }

  static shouldRedirectToHub(idToken: string): boolean {
    if (!idToken) {
      return false;
    }

    try {
      const decoded = jwtDecode<DecodedUserTypeIdToken>(idToken);
      return decoded.user_type === 'HUB';
    } catch (err) {
      console.error('Error decoding id_token in shouldRedirectToHub:', err);
      return false;
    }
  }

  static setCognitoConfiguration(config: DynamicCognitoConfig): void {
    Cognito.setConfiguration(config);
    HelpSiteCookieHelper.setCognitoVariables(config);
    setCognitoVariables(config);
  }
}
